import React, { useRef, useEffect, useState } from "react";
import { ArrowUp, Paperclip, Sparkles, Mic } from "lucide-react";
import { toast } from "sonner";

export default function PromptComposer({ value, onChange, onSend, disabled, placeholder }) {
  const ref = useRef(null);
  const recRef = useRef(null);
  const baseRef = useRef("");
  const [listening, setListening] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 200) + "px";
  }, [value]);

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (!disabled && value.trim()) onSend();
    }
  };

  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice input isn't supported in this browser");
      return;
    }
    if (listening) {
      recRef.current && recRef.current.stop();
      return;
    }
    const r = new SR();
    r.lang = "en-US";
    r.interimResults = true;
    r.continuous = false;
    baseRef.current = value ? value.trim() + " " : "";
    r.onresult = (e) => {
      let txt = "";
      for (let i = 0; i < e.results.length; i++) txt += e.results[i][0].transcript;
      onChange(baseRef.current + txt);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    recRef.current = r;
    setListening(true);
    r.start();
  };

  return (
    <div className="px-4 pb-5 pt-2">
      <div className="mx-auto max-w-3xl">
        <div className="rounded-3xl border border-[#E6E6DF] bg-white shadow-[0_2px_20px_rgba(23,23,22,0.05)] p-2.5 focus-within:border-[#CECEBE]" style={{ transition: "border-color 0.2s ease" }}>
          <textarea
            ref={ref}
            rows={1}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKey}
            placeholder={placeholder || "Message OmniAI…"}
            data-testid="prompt-input"
            className="w-full resize-none bg-transparent px-3 py-2 text-[15px] leading-relaxed placeholder:text-[#8C8C85] focus:outline-none omni-scroll"
          />
          <div className="flex items-center justify-between px-1 pt-1">
            <div className="flex items-center gap-1.5">
              <button className="h-9 w-9 rounded-full flex items-center justify-center text-[#8C8C85] hover:bg-[#EFEFEA]" style={{ transition: "background-color 0.2s ease" }} title="Attach" data-testid="attach-button">
                <Paperclip className="h-[18px] w-[18px]" />
              </button>
              <button
                onClick={toggleVoice}
                className={`h-9 w-9 rounded-full flex items-center justify-center ${listening ? "bg-red-50 text-red-600 ring-2 ring-red-200" : "text-[#8C8C85] hover:bg-[#EFEFEA]"}`}
                style={{ transition: "background-color 0.2s ease" }}
                title={listening ? "Stop dictation" : "Voice input"}
                data-testid="voice-input-button"
              >
                <Mic className="h-[18px] w-[18px]" />
              </button>
              <button className="h-9 px-3 rounded-full flex items-center gap-1.5 text-[#5F5F5B] hover:bg-[#EFEFEA] text-sm" style={{ transition: "background-color 0.2s ease" }} title="Enhance prompt" data-testid="enhance-button">
                <Sparkles className="h-[16px] w-[16px]" /> Enhance
              </button>
            </div>
            <button
              onClick={onSend}
              disabled={disabled || !value.trim()}
              data-testid="send-button"
              className="h-9 w-9 rounded-full flex items-center justify-center bg-[#1F2421] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#2D6A4F]"
              style={{ transition: "background-color 0.2s ease, opacity 0.2s ease" }}
            >
              <ArrowUp className="h-[18px] w-[18px]" strokeWidth={2.4} />
            </button>
          </div>
        </div>
        <p className="mt-2 text-center text-[11px] text-[#8C8C85]">
          {listening ? "Listening… speak now" : "OmniAI can make mistakes. Verify important information."}
        </p>
      </div>
    </div>
  );
}
